import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { getOrder, type OrderSummary } from '@/lib/checkoutClient';
import { isMockMode, getMockOrder } from '@/lib/mock-data';

/**
 * useOrderDetail — detalle completo de una orden (`GET /v1/orders/:id`),
 * incluyendo `items` y `qrCodes`.
 *
 * El listado `GET /v1/orders/mine` devuelve `OrderOut` (sin items ni QRs); sólo
 * el detalle (`OrderDetailOut`) los trae. Este hook hidrata esa información a
 * demanda (cantidad de items, botón "Ver QR", modal de QRs) sin polling, con
 * cache compartida con `useOrderPolling` (misma queryKey `['order', id]`).
 *
 * `enabled` permite no disparar el fetch para órdenes donde no aporta (ej.
 * canceladas/expiradas sin QRs), o hasta que el usuario lo necesite.
 */
export function useOrderDetail(
  orderId: string | undefined,
  enabled = true,
): UseQueryResult<OrderSummary> {
  return useQuery({
    queryKey: ['order', orderId, isMockMode() ? 'mock' : 'live'],
    queryFn: async () => {
      if (!orderId) throw new Error('useOrderDetail: orderId required');
      if (isMockMode()) {
        const mock = getMockOrder(orderId);
        if (!mock) {
          throw Object.assign(new Error('Mock order not found'), {
            status: 404,
            code: 'ORDER_NOT_FOUND',
          });
        }
        return mock;
      }
      return getOrder(orderId);
    },
    enabled: enabled && !!orderId,
    staleTime: 30_000,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | null)?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}
